import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {AgGridReact} from 'ag-grid-react';
import {AllCommunityModule, ModuleRegistry, themeQuartz} from 'ag-grid-community';
import type {
    CellClickedEvent,
    ColDef,
    FocusGridInnerElementParams,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    IsFullWidthRowParams,
    PostSortRowsParams,
    ProcessRowParams,
    RowClassParams,
    RowHeightParams,
    ValueFormatterParams,
    ValueGetterParams
} from 'ag-grid-community';
import {CheckIcon, CircleAlert, Info, XIcon} from 'lucide-react';
import Checkbox from 'components/common/checkbox.component';
import {Button, ButtonType} from 'components/common/Button.component';
import {ICON_SIZE} from 'components/common/constants';
import {DOCUMENT_TYPE, TASK_ACTIONS, TASK_DETAILS_URL_PART, TASK_OVERDUE_STATUS} from 'utils/constants';
import {formatDocumentType, getTaskOverdueStatus} from 'utils/valueFormatter.function';
import {TaskCommentsCell} from './TaskCommentsCell.component';
import {MyTasksActions} from './AvailableActions.class';
import {formatTotal, normalizeTaskAmount} from './taskAmounts.function';
import {
    CELL_TYPE,
    FIELD_META,
    FROM_FIELD,
    ROW_TYPE,
    buildRowData,
    displayValue,
    fieldLabel,
    getRowId,
    regroupSortedNodes,
    sortValue
} from './tasksGrid.function';
import {
    bodyTabStops,
    headerCellControl,
    pageStopOutside,
    renderedRows,
    rowIndexOf,
    rowTabStops
} from './tasksGridFocus.function';
import type {GridRow} from './tasksGrid.function';
import type {TaskGroup} from './tasksGrouping.function';
import type {MyTask, TaskWrapper, Translate} from 'types/myTasks';
import './tasksGrid.scss';

// ag-grid v33+ requires explicit module registration; Community only, so no licence key
ModuleRegistry.registerModules([AllCommunityModule]);

// the group row toggles on click, except on these: the checkbox selects the group's tasks and
// Approve all acts on them
const GROUP_CONTROLS = '.tasks-grid__group-select, .tasks-grid__group-approve';

const SELECT_COL = 'select';
const ACTIONS_COL = 'actions';

// The flat list marks its rows and cells with these, and the smoke tests address them by name.
// ag-grid builds the row and its plain text cells itself, so they are stamped on afterwards -
// see processRowPostCreate below. Cells rendered by this file carry their own data-cy inline.
const ROW_DATA_CY = Object.freeze({task: 'mytasks.task', group: 'myTasks.group.row'});
const CELL_DATA_CY: Readonly<Record<string, string>> = Object.freeze({
    [FROM_FIELD]: 'myTasks.task.supplierName',
    description: 'myTasks.task.description',
    companyName: 'myTasks.task.companyName'
});

const ROW_HEIGHT = Object.freeze({
    task: 56,
    group: 44,
    header: 40
});

// the list opens on the latest document due date; the grid owns its sorting from there on
const DEFAULT_SORT = Object.freeze({colId: 'documentDueDate', sort: 'desc' as const});

// the action bar is fixed to the bottom, so the grid has to stop above it
const ACTION_BAR_HEIGHT = 60;
const MIN_GRID_HEIGHT = 200;

// a row tabbed to from outside the rendered window is only drawn a frame or two after the scroll
const FOCUS_RENDER_ATTEMPTS = 5;

interface TasksGridProps {
    groups: TaskGroup[];
    collapsedGroups?: Record<string, boolean>;
    visibleFields: string[];
    translate: Translate;
    allSelected?: boolean;
    onToggleAll: () => void;
    onToggleTask: (key: string) => void;
    onToggleGroup: (groupKey: string) => void;
    onToggleGroupSelection: (wrappers: TaskWrapper[]) => void;
    onApproveGroup: (wrappers: TaskWrapper[]) => void;
    taskAction: (action: string, keys: string[], options?: {hasDraftComment?: boolean}) => void;
}

/**
 * The grid fills whatever is left below it: the payment batch and the hidden-tasks warning
 * push it down when they show, and their heights are not known up front.
 */
function useAvailableHeight(elementRef: React.RefObject<HTMLElement>): number | null {
    const [height, setHeight] = useState<number | null>(null);

    useLayoutEffect(() => {
        const measure = () => {
            const element = elementRef.current;
            if (!element) return;
            const top = element.getBoundingClientRect().top;
            setHeight(Math.max(MIN_GRID_HEIGHT, window.innerHeight - top - ACTION_BAR_HEIGHT));
        };

        measure();
        window.addEventListener('resize', measure);
        // the blocks above the grid live in the page container, not in the grid's own parent:
        // watching it is what catches them showing and hiding, so this subscribes once
        const container = elementRef.current?.closest('.ast_task_list');
        const observer = container && typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(measure)
            : null;
        observer?.observe(container);

        return () => {
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, [elementRef]);

    return height;
}

// Gaia-flavoured Quartz; the colours are the ones Gaia's own ag-grid theme uses, while the
// sizes stay literal - Gaia's rem-based tokens are authored for a 16px root, not this app's 10px one
const gridTheme = themeQuartz.withParams({
    accentColor: 'var(--ga-color-primary)',
    borderColor: 'var(--ga-color-neutral-30)',
    browserColorScheme: 'light',
    fontFamily: 'inherit',
    fontSize: '13px',
    headerFontSize: '12px',
    headerFontWeight: 600,
    headerBackgroundColor: 'var(--ga-color-neutral-10)',
    headerCellHoverBackgroundColor: 'var(--ga-color-neutral-20)',
    oddRowBackgroundColor: 'transparent',
    rowHoverColor: 'var(--ga-color-surface-action-hover-2)',
    columnHoverColor: 'color-mix(in srgb, var(--ga-color-primary) 8%, transparent)',
    selectedRowBackgroundColor: 'var(--ga-color-surface-selected)',
    // the dark bubble the shared Gaia tooltip uses everywhere else (see AppTooltip.component)
    tooltipBackgroundColor: 'var(--ga-color-surface-action-hover)',
    tooltipTextColor: 'var(--ga-color-text-on-action)',
    tooltipBorder: false,
    wrapperBorderRadius: '4px'
});

/**
 * ag-grid rendering of the my-tasks list.
 *
 * Grouping is drawn with full-width rows rather than ag-grid row grouping, which is an
 * Enterprise-only feature.
 */
const TasksGrid = ({
                       groups,
                       collapsedGroups,
                       visibleFields,
                       translate,
                       allSelected,
                       onToggleAll,
                       onToggleTask,
                       onToggleGroup,
                       onToggleGroupSelection,
                       onApproveGroup,
                       taskAction
                   }: TasksGridProps) => {

    const navigate = useNavigate();
    const fields = visibleFields;

    const rowData = useMemo(
        () => buildRowData(groups, collapsedGroups, translate),
        [groups, collapsedGroups, translate]
    );

    const selectRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        if (params.data?.rowType !== ROW_TYPE.TASK) return null;
        const task = params.data.task;
        return (
            <Checkbox
                inputId={'task-checkbox-' + task.id}
                onChange={(event) => {
                    event.stopPropagation();
                    onToggleTask(task.key);
                }}
                isChecked={Boolean(params.data.selected)}
                isDisabled={task.isHandled}
                dataCy="myTasks.task.select"
                ariaLabel={translate('myTasks.task.selectTaskOf', DOCUMENT_TYPE.asString(task.documentType), task.companyName || '')}
            />
        );
    }, [onToggleTask, translate]);

    const selectAllHeader = useCallback(() => (
        <Checkbox
            inputId="tasks-grid-select-all"
            onChange={onToggleAll}
            isChecked={Boolean(allSelected)}
            dataCy="myTasks.selectAll"
            ariaLabel={translate('myTasks.selectAllTasks')}
        />
    ), [allSelected, onToggleAll, translate]);

    const documentTypeRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        const task = params.data?.task;
        if (!task) return null;
        return (
            <span className={formatDocumentType(task.documentType, task.amount)}
                  title={translate('documentType.' + DOCUMENT_TYPE.asString(task.documentType))}
                  data-cy="myTasks.task.type"/>
        );
    }, [translate]);

    const dueDateRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        const task = params.data?.task;
        if (!task) return null;
        const status = getTaskOverdueStatus(task.documentDueDate);
        const overdue = status.overdueClass === TASK_OVERDUE_STATUS.TASK_OVERDUE;
        return (
            <span className="tasks-grid__due" data-cy="myTasks.task.documentDueDate">
                {params.valueFormatted ?? params.value}
                {overdue &&
                    <CircleAlert size={ICON_SIZE.SMALL} className="ms-1"
                                 aria-label={translate('myTasks.task.taskOverdue', status.dueDate, params.value)}
                                 style={{color: 'var(--ga-color-icon-error)'}}/>
                }
            </span>
        );
    }, [translate]);

    const amountRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        const task = params.data?.task;
        if (!task) return null;
        const amounts = normalizeTaskAmount(task, translate);
        return (
            <span className="tasks-grid__amount">
                <span className="tasks-grid__amount-main">
                    <span className={task.amount < 0 ? 'text-danger' : ''} data-cy="myTasks.task.amount">
                        {amounts.amount}
                    </span>
                    <span className="tasks-grid__currency" data-cy="myTasks.task.currency">{amounts.unit}</span>
                </span>
                {amounts.foreignAmount &&
                    <span className="tasks-grid__foreign" data-cy="myTasks.task.foreignAmount">
                        {amounts.foreignAmount} {amounts.foreignCurrency}
                    </span>
                }
            </span>
        );
    }, [translate]);

    const commentsRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        const task = params.data?.task;
        if (!task) return null;
        if (!task.numberOfComments && !task.hasDraftComment) return null;
        return <TaskCommentsCell task={task} translate={translate}/>;
    }, [translate]);

    const actionsRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        const task = params.data?.task;
        if (!task) return null;

        if (task.isHandled) {
            return <Info size={ICON_SIZE.MEDIUM} className="handled-info"
                         aria-label={translate('myTasks.taskWasHandled')}/>;
        }

        const actions = new MyTasksActions(task);
        // review replaces the quick actions, exactly as the flat list did
        const reviewRow = actions.isEnabled(TASK_ACTIONS.REVIEW);

        return (
            <span className="tasks-grid__actions">
                {reviewRow ?
                    <Button
                        type={ButtonType.SECONDARY}
                        onClick={() => taskAction(TASK_ACTIONS.REVIEW, [task.key])}
                        dataCy={'task.action.' + translate('myTasks.actionBar.review')}
                        text={translate('myTasks.actionBar.review')}
                    /> :
                    <>
                        {task.taskActions?.includes('quick-approve') &&
                            <Button
                                type={ButtonType.GHOST}
                                onClick={() => taskAction(TASK_ACTIONS.APPROVE, [task.key], {hasDraftComment: task.hasDraftComment})}
                                iconOnly={true}
                                icon={<CheckIcon size={ICON_SIZE.MEDIUM}/>}
                                ariaLabel={translate('myTasks.task.approve')}
                                dataCy={'task.action.' + translate('myTasks.task.approve')}
                            />
                        }
                        {task.taskActions?.includes('quick-reject') &&
                            <Button
                                type={ButtonType.GHOST}
                                onClick={() => taskAction(TASK_ACTIONS.REJECT, [task.key])}
                                iconOnly={true}
                                icon={<XIcon size={ICON_SIZE.MEDIUM}/>}
                                ariaLabel={translate('myTasks.task.reject')}
                                dataCy={'task.action.' + translate('myTasks.task.reject')}
                            />
                        }
                    </>
                }
            </span>
        );
    }, [taskAction, translate]);

    const RENDERERS: Record<string, (params: ICellRendererParams<GridRow>) => React.ReactElement | null> =
        useMemo(() => ({
            documentType: documentTypeRenderer,
            amount: amountRenderer,
            numberOfComments: commentsRenderer,
            documentDueDate: dueDateRenderer
        }), [documentTypeRenderer, amountRenderer, commentsRenderer, dueDateRenderer]);

    const columnDefs = useMemo<ColDef<GridRow>[]>(() => {
        const dataColumns: ColDef<GridRow>[] = fields.map(field => {
            const meta = FIELD_META[field];
            const isNumeric = meta?.type === CELL_TYPE.AMOUNT || meta?.type === CELL_TYPE.NUMBER;
            const width = meta?.width ?? 140;
            const renderer = RENDERERS[field];
            return {
                colId: field,
                headerName: fieldLabel(field, translate),
                width,
                // a column narrower than the shared minimum keeps the width FIELD_META asked for
                minWidth: Math.min(80, width),
                type: isNumeric ? 'rightAligned' : undefined,
                // "initial" so it only seeds the opening sort: a plain `sort` would come back
                // on every column rebuild and overrule whatever the user sorted by
                initialSort: field === DEFAULT_SORT.colId ? DEFAULT_SORT.sort : undefined,
                // the row is a wrapper, so every value is read off `data.task`
                valueGetter: (params: ValueGetterParams<GridRow>) => sortValue(params.data?.task, field),
                valueFormatter: (params: ValueFormatterParams<GridRow>) => displayValue(params.data?.task, field, translate),
                cellRenderer: renderer,
                // ag-grid cannot spot truncation inside a cell renderer, so such a column would show
                // its tooltip on every hover; the doc type icon carries its own title, the rest read
                // fine as they are
                tooltipValueGetter: renderer
                    ? undefined
                    : (params) => displayValue(params.data?.task, field, translate)
            };
        });

        return [
            {
                colId: SELECT_COL,
                headerName: '',
                width: 44,
                minWidth: 44,
                maxWidth: 44,
                pinned: 'left',
                sortable: false,
                resizable: false,
                cellClass: 'tasks-grid__select-cell',
                headerClass: 'tasks-grid__select-header',
                cellRenderer: selectRenderer,
                headerComponent: selectAllHeader,
                headerTooltip: translate('myTasks.selectAllTasks')
            },
            ...dataColumns,
            {
                colId: ACTIONS_COL,
                headerName: translate('myTasks.column.action'),
                width: 10,
                minWidth: 120,
                pinned: 'right',
                sortable: false,
                cellClass: 'tasks-grid__actions-cell',
                cellRenderer: actionsRenderer
            }
        ];
    }, [fields, translate, RENDERERS, selectRenderer, selectAllHeader, actionsRenderer]);

    const groupRowRenderer = useCallback((params: ICellRendererParams<GridRow>) => {
        const row = params.data;
        const groupSelected = row.wrappers.length > 0 && row.wrappers.every(wrapper => wrapper.selected);
        // the whole header row toggles the group, so the controls that mean something else
        // have to opt out
        const toggleGroup = (event: React.MouseEvent<HTMLDivElement>) => {
            if ((event.target as HTMLElement).closest(GROUP_CONTROLS)) return;
            onToggleGroup(row.groupKey);
        };
        return (
            <div className={'tasks-grid__group' + (row.collapsed ? ' collapsed' : '')} data-cy="myTasks.group"
                 onClick={toggleGroup}>
                <span className="tasks-grid__group-select">
                    <Checkbox
                        inputId={'group-checkbox-' + row.groupKey}
                        onChange={() => onToggleGroupSelection(row.wrappers)}
                        isChecked={groupSelected}
                        dataCy="myTasks.group.select"
                        ariaLabel={translate('myTasks.overview.selectGroup', row.label)}
                    />
                </span>
                <button type="button"
                        className={'tasks-grid__group-toggle' + (row.collapsed ? ' collapsed' : '')}
                        aria-expanded={!row.collapsed}
                        data-cy="myTasks.group.toggle">
                    <span className="tasks-grid__group-chevron" aria-hidden="true"/>
                    <span className="tasks-grid__group-title">{row.label}</span>
                    <span className="ga-badge ga-badge--text ga-badge--muted">{row.count}</span>
                </button>
                <span className="tasks-grid__group-total">{row.totals.map(formatTotal).join(', ')}</span>
                {!row.collapsed && row.approvable.length > 0 &&
                    <Button
                        type={ButtonType.GHOST}
                        onClick={() => onApproveGroup(row.wrappers)}
                        extraClassName="tasks-grid__group-approve"
                        dataCy="myTasks.group.approveAll"
                        text={translate('myTasks.overview.approveAll')}
                    />
                }
            </div>
        );
    }, [onToggleGroup, onToggleGroupSelection, onApproveGroup, translate]);

    /**
     * ag-grid owns the row element and its plain cells, so the hooks the e2e suite needs are
     * added here - the callback ag-grid documents for exactly this. Every row passes through it
     * as it is created, so filtering and sorting keep them in place.
     */
    const processRowPostCreate = useCallback((params: ProcessRowParams<GridRow>) => {
        const isTask = params.node.data?.rowType === ROW_TYPE.TASK;
        params.eRow.setAttribute('data-cy', isTask ? ROW_DATA_CY.task : ROW_DATA_CY.group);
        if (!isTask) return;
        // the row is the stop that opens the task, and carries the tab index from the moment it
        // exists - ag-grid adds its own a frame later, by when the tab has moved on. A handled task
        // cannot be opened, so its row stays out, as in the flat list
        params.eRow.setAttribute('tabindex', params.node.data.task.isHandled ? '-1' : '0');
        for (const [colId, dataCy] of Object.entries(CELL_DATA_CY)) {
            params.eRow.querySelector('[col-id="' + colId + '"]')?.setAttribute('data-cy', dataCy);
        }
    }, []);

    /**
     * The classes that follow the row's state. getRowClass is read once, when the row is built,
     * so a class it hands out is never taken back - these are re-evaluated as the data changes.
     */
    const rowClassRules = useMemo(() => {
        const isSelected = (params: RowClassParams<GridRow>) => Boolean(params.data?.selected);
        return {
            'tasks-grid__row--selected': isSelected,
            // the marker the flat list uses, so one selector serves both versions
            'selected-row': isSelected,
            'tasks-grid__row--handled': (params: RowClassParams<GridRow>) => Boolean(params.data?.task?.isHandled),
            'tasks-grid__row--new': (params: RowClassParams<GridRow>) => Boolean(params.data?.task?.isNew)
        };
    }, []);

    const groupOrder = useMemo(() => groups.map(group => group.key), [groups]);

    const postSortRows = useCallback(
        (params: PostSortRowsParams<GridRow>) => regroupSortedNodes(params.nodes, groupOrder),
        [groupOrder]
    );

    const gridRef = useRef<HTMLDivElement>(null);
    const gridApiRef = useRef<GridApi<GridRow> | null>(null);
    const gridHeight = useAvailableHeight(gridRef);

    const openTask = useCallback((task?: MyTask) => {
        if (!task || task.isHandled) return;
        navigate(TASK_DETAILS_URL_PART + task.key);
    }, [navigate]);

    const onCellClicked = useCallback((event: CellClickedEvent<GridRow>) => {
        if (event.data?.rowType !== ROW_TYPE.TASK) return;
        if (event.column.getColId() === SELECT_COL || event.column.getColId() === ACTIONS_COL) return;
        openTask(event.data.task);
    }, [openTask]);

    // the row is a tab stop of its own, and opens the task the way a click on it does
    const onRowKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        if (event.key !== 'Enter' || !target.classList?.contains('tasks-grid__task-row')) return;
        openTask(gridApiRef.current?.getDisplayedRowAtIndex(rowIndexOf(target))?.data?.task);
    }, [openTask]);

    // focus one end of a row, scrolling to it first: a row outside the rendered window is only
    // drawn a frame or two after the scroll
    const focusRow = useCallback((index: number, backwards: boolean, attempts = FOCUS_RENDER_ATTEMPTS): boolean => {
        const root = gridRef.current;
        const api = gridApiRef.current;
        if (!root || !api || index < 0 || index >= api.getDisplayedRowCount()) return false;

        // a handled task holds no stop at all - its row is not navigable and its controls are
        // disabled - so the walk carries on past it
        const data = api.getDisplayedRowAtIndex(index)?.data;
        if (data?.task?.isHandled) return focusRow(index + (backwards ? -1 : 1), backwards, attempts);

        api.ensureIndexVisible(index);
        const row = renderedRows(root).find(candidate => rowIndexOf(candidate) === index);
        const stops = row ? rowTabStops(row) : [];
        if (stops.length > 0) {
            (backwards ? stops[stops.length - 1] : stops[0]).focus();
            return true;
        }
        // the row is still being drawn: scrolling to it, and filling its cells, takes a frame
        if (attempts > 0) requestAnimationFrame(() => focusRow(index, backwards, attempts - 1));
        return true;
    }, []);

    // shift+tab in from the page below: ag-grid's tab guard would jump to the header, over every row
    const focusGridInnerElement = useCallback(
        (params: FocusGridInnerElementParams<GridRow>) => (
            params.fromBottom && focusRow(params.api.getDisplayedRowCount() - 1, true)
        ),
        [focusRow]
    );

    /**
     * Tab order: the header cells, then row by row - the row itself, then the controls it holds
     * (see tasksGridFocus.function). ag-grid gives the rows no stops of its own while cell focus is
     * suppressed, so the walk is driven here, in the capture phase ahead of ag-grid's own handlers,
     * and the event is only stopped for the steps taken over.
     */
    useEffect(() => {
        const root = gridRef.current;
        if (!root) return;

        const focusStop = (stop: HTMLElement | undefined, event: KeyboardEvent): boolean => {
            if (!stop) return false;
            event.preventDefault();
            event.stopPropagation();
            stop.focus();
            return true;
        };

        const onKeyDown = (event: KeyboardEvent) => {
            const api = gridApiRef.current;
            const target = event.target as HTMLElement | null;
            if (event.key !== 'Tab' || event.ctrlKey || event.altKey || event.metaKey) return;
            if (!api || !target?.closest) return;

            const backwards = event.shiftKey;
            const columns = api.getAllDisplayedColumns();
            const lastColumn = columns[columns.length - 1];

            const headerCell = target.closest<HTMLElement>('.ag-header-cell');
            if (headerCell) {
                // ag-grid walks the header cells itself; what it does not do is step into the
                // controls they hold - the select-all checkbox - or on into the rows
                if (target === headerCell && focusStop(headerCellControl(root, columns, headerCell, backwards), event)) return;
                if (backwards || headerCell.getAttribute('col-id') !== lastColumn?.getColId()) return;
                if (focusRow(0, false)) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                return;
            }

            const stops = bodyTabStops(root);
            const current = stops.indexOf(target);
            if (current === -1) return;
            if (focusStop(stops[current + (backwards ? -1 : 1)], event)) return;

            // the end of the rendered rows: the next row has to be scrolled into view first
            const rows = renderedRows(root);
            const edge = backwards ? rows[0] : rows[rows.length - 1];
            if (edge && focusRow(rowIndexOf(edge) + (backwards ? -1 : 1), backwards)) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            // the list ends here: back into the header, or on to the page below the grid
            if (backwards && lastColumn) {
                event.preventDefault();
                event.stopPropagation();
                api.setFocusedHeader(lastColumn);
                return;
            }
            focusStop(pageStopOutside(root, backwards), event);
        };

        root.addEventListener('keydown', onKeyDown, true);
        return () => root.removeEventListener('keydown', onKeyDown, true);
    }, [focusRow]);

    return (
        <div className="tasks-grid" data-cy="myTasks.grid" ref={gridRef} onKeyDown={onRowKeyDown}
             style={gridHeight ? {height: gridHeight} : undefined}>
            <AgGridReact<GridRow>
                theme={gridTheme}
                onGridReady={(event: GridReadyEvent<GridRow>) => (gridApiRef.current = event.api)}
                focusGridInnerElement={focusGridInnerElement}
                rowData={rowData}
                columnDefs={columnDefs}
                getRowId={(params: GetRowIdParams<GridRow>) => getRowId(params.data)}
                headerHeight={ROW_HEIGHT.header}
                getRowHeight={(params: RowHeightParams<GridRow>) => (params.data?.rowType === ROW_TYPE.GROUP ? ROW_HEIGHT.group : ROW_HEIGHT.task)}
                isFullWidthRow={(params: IsFullWidthRowParams<GridRow>) => params.rowNode.data?.rowType === ROW_TYPE.GROUP}
                fullWidthCellRenderer={groupRowRenderer}
                getRowClass={(params: RowClassParams<GridRow>) => (
                    // what the row is; what it is currently in is in rowClassRules, and the
                    // data-cy hooks are stamped on in processRowPostCreate
                    params.data?.task ? 'tasks-grid__task-row' : 'tasks-grid__group-row'
                )}
                rowClassRules={rowClassRules}
                onCellClicked={onCellClicked}
                postSortRows={postSortRows}
                processRowPostCreate={processRowPostCreate}
                // a column scrolled out of view is still rendered, so its cell can be read
                // without scrolling the grid to it first. The column set is a fixed, bounded
                // list (FIELD_META), so this stays a handful of extra cells per row
                suppressColumnVirtualisation={true}
                defaultColDef={{sortable: true, resizable: true}}
                suppressCellFocus={true}
                // the tab order walks the rows in display order, so the DOM has to be in that
                // order too - for the reader as much as for the fallback the browser provides
                ensureDomOrder={true}
                tooltipShowDelay={300}
                // the values are readable in the cell; only a clipped one needs spelling out
                tooltipShowMode="whenTruncated"
                localeText={{noRowsToShow: translate('myTasks.noTasksFound')}}
                domLayout="normal"
            />
        </div>
    );
};

export default TasksGrid;
